import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import useAutofocus from "../../hooks/useAutofocus";
import { pxToRem } from "@input-output-hk/px-to-rem";
import colors from "../../tokens/colors";
import { shadows } from "../../tokens/shadows";
import { trueOrUndef } from "../../utils/dom-attribute-helpers";

const TextAreaElement = styled.textarea`
  appearance: none;
  resize: none;

  font: inherit;
  color: var(--input-text-color);
  width: 100%;
  padding: ${pxToRem(16)} var(--spacing-large);
  background: #fefeff;
  border: ${pxToRem(1)} solid #ffffff;
  box-sizing: border-box;
  box-shadow: ${shadows.elevation1};
  border-radius: ${pxToRem(10)};
  &::placeholder {
    color: ${colors.secondary.ashGrey};
  }
  ::-ms-clear {
    display: none;
  }
  overflow: hidden;
  height: ${pxToRem(60)};
  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
  }
  &[aria-invalid] {
    border-color: var(--input-error-border-color);
  }
`;

type Props = React.ComponentPropsWithoutRef<typeof TextAreaElement> & {
  autogrow?: boolean;
  autoFocus?: boolean;
};
const TextArea: React.FC<Props> = React.forwardRef(
  ({ autogrow, autoFocus = false, ...props }, givenRef) => {
    const ourRef = useRef<HTMLTextAreaElement>(null);
    const ref = (givenRef ||
      ourRef) as React.MutableRefObject<HTMLTextAreaElement>;
    useAutofocus(ref, autoFocus);
    useEffect(() => {
      const el = ref.current;
      if (autogrow && el) {
        /**
         * We could potentially use an offscreen clone
         * to calc the height if this becomes unperformant
         */
        const calcHeight = () => {
          el.style.setProperty("height", "auto", "important");
          const newHeight = el.scrollHeight; // Trigger DOM Paint
          el.style.setProperty("height", newHeight + "px", "important");
          return newHeight;
        };
        el.addEventListener("input", calcHeight);
        calcHeight();
        return () => el.removeEventListener("input", calcHeight);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autogrow]);
    return (
      <TextAreaElement
        rows={1}
        data-autogrow={trueOrUndef(autogrow)}
        ref={ref}
        {...props}
      />
    );
  }
);

TextArea.displayName = "TextArea";

export default TextArea;
